import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { underscore } from '@ember/string';
import { cached, tracked } from '@glimmer/tracking';

import type { Billing, PopupData, RequestError, Service } from 'webapp-types';
import { BillingAddonType, BillingServiceType, PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import AccountBillingAddonsOptionComponent from 'webapp/components/account/billing/addons-option';
import AccountBillingServicesOptionComponent from 'webapp/components/account/billing/services-option';
import AccountBillingSummaryRowComponent from 'webapp/components/account/billing/summary-row';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import { Severity } from 'webapp/services/logger';

enum PopupStep {
	SERVICES = 'services',
	ADDONS = 'addons',
	SUMMARY = 'summary',
}

export default class BillingPopupServicesAndAddonsComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked selectedService: Billing.ServiceInfo | null = null;
	@tracked selectedAddons: Billing.Addon[] = [];
	@tracked step = PopupStep.ADDONS;

	isSummaryFinished = false;

	steps: PopupStep[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const steps: PopupStep[] = [];

		if (!this.billingService.isTrial) {
			steps.push(PopupStep.SERVICES);
			this.step = PopupStep.SERVICES;
		}

		if (this.addonOptions.length) {
			steps.push(PopupStep.ADDONS);
		}

		if (!steps.length) {
			void this.args.model.close();
			return;
		}

		this.steps = [...steps, PopupStep.SUMMARY];

		this.args.model.on(PopupEvent.CLOSING, this, () => {
			if (!this.isSummaryFinished) {
				const trackingEvent = this.trackingEventForStep;

				if (trackingEvent) {
					const type = this.billingService.getTrackingSubscriptionType(),
						choice = ['skip'];

					this.analyticsService.event(trackingEvent, { choice, type });
				}
			}
		});
	}

	get title() {
		let title = 'Select';
		if (this.isServiceListShown) {
			title = `${title} a service option`;
		} else if (this.isAddonListShown) {
			title = `${title} an add-on option`;
		} else {
			title = 'Summary';
		}

		return title;
	}

	get text() {
		if (this.isServiceListShown) {
			return `We offer several services designed for organizations like yours:`;
		} else if (this.isAddonListShown) {
			return 'Elevate your experience with a variety of tailored add-ons:';
		} else {
			return 'Confirm your selection:';
		}
	}

	get serviceOptions() {
		return this.billingService.prepareServicesList();
	}

	get addonOptions() {
		return this.billingService
			.prepareAddonsList()
			.filter((addon) => !addon.isEnabled)
			.filter(
				(addon) =>
					![
						BillingAddonType.PRIORITY_SYNC,
						BillingAddonType.AI_CREDITS,
						BillingAddonType.CLIENT_PACK,
					].includes(addon.id),
			);
	}

	get isServiceListShown() {
		return this.step === PopupStep.SERVICES;
	}

	get isAddonListShown() {
		return this.step === PopupStep.ADDONS;
	}

	get isSummaryShown() {
		return this.step === PopupStep.SUMMARY;
	}

	get isSummaryEmpty() {
		return !this.selectedService && !this.selectedAddons.length;
	}

	get showText() {
		return (
			this.isServiceListShown ||
			this.isAddonListShown ||
			(this.isSummaryShown && !this.isSummaryEmpty)
		);
	}

	get trackingChoiceForService() {
		const service = this.selectedService;
		if (service) {
			return [underscore(String(service.id))];
		} else {
			return ['skip'];
		}
	}

	@cached
	get trackingTypeForAddons() {
		const addons = this.selectedAddons,
			choice = [];
		if (addons.length) {
			addons.forEach((addon) => {
				choice.push(underscore(addon.id));
			});
		} else {
			choice.push('skip');
		}

		return choice;
	}

	get trackingEventForStep() {
		const currentStep = this.step;

		if (currentStep === PopupStep.SERVICES) {
			return TrackingEvent.SERVICES_AND_ADDON_POPUP_SERVICE_STEP_DONE;
		} else if (currentStep === PopupStep.ADDONS) {
			return TrackingEvent.SERVICES_AND_ADDON_POPUP_ADDONS_STEP_DONE;
		} else if (currentStep === PopupStep.SUMMARY) {
			return TrackingEvent.SERVICES_AND_ADDON_POPUP_SUMMARY_STEP_DONE;
		}

		return null;
	}

	get showBackButton() {
		return this.step !== this.steps[0];
	}

	get isResellerPartnerOnly() {
		return (
			this.selectedService?.id === BillingServiceType.RESELLER_PARTNER &&
			!this.selectedAddons.length
		);
	}

	get finishButtonText() {
		if (this.isResellerPartnerOnly) {
			return 'Book a call';
		} else {
			return 'Complete purchase';
		}
	}

	trackCurrentStepEvent() {
		const currentStep = this.step;

		let choice;
		if (currentStep === PopupStep.SERVICES) {
			choice = this.trackingChoiceForService;
		} else if (currentStep === PopupStep.ADDONS) {
			choice = this.trackingTypeForAddons;
		} else if (currentStep === PopupStep.SUMMARY) {
			choice = [...this.trackingChoiceForService, ...this.trackingTypeForAddons];
		}

		const type = this.billingService.getTrackingSubscriptionType(),
			trackingEvent = this.trackingEventForStep;

		if (trackingEvent) {
			this.analyticsService.event(trackingEvent, { choice, type });
		}
	}

	get canBookACall(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	contactForResellerPartnerHelp() {
		window.open(this.accountService.resellerPartnerUrl, '_blank');
	}

	@action
	chooseService(option: Billing.ServiceInfo | null) {
		this.selectedService = option;
	}

	@action
	finishSummary() {
		if (this.submitInProgress) {
			return;
		}

		this.isSummaryFinished = true;

		if (this.isResellerPartnerOnly) {
			if (this.canBookACall) {
				this.contactForResellerPartnerHelp();
			}
			this.close();
		} else {
			this.submitInProgress = true;

			const layer = this.infolayerService.loading('Confirming your purchase...'),
				selectedService = this.selectedService,
				selectedAddons = this.selectedAddons,
				promises = [];

			if (selectedService?.isPurchasable) {
				promises.push(this.billingService.addService(selectedService.id));
			} else if (selectedService?.id === BillingServiceType.RESELLER_PARTNER) {
				this.contactForResellerPartnerHelp();
			}

			selectedAddons.forEach((addon) => {
				promises.push(this.billingService.addAddon(addon.id));
			});

			Promise.all(promises)
				.then(
					() => {
						const serviceText = 'Service',
							addonText = `Add-on${selectedAddons.length > 1 ? 's' : ''}`,
							restOfTheText =
								'successfully purchased! We will contact you soon with more details';

						let finalText = '';
						if (selectedService) {
							finalText = serviceText;
						}

						if (selectedAddons.length) {
							if (finalText) {
								finalText = `${serviceText} and ${addonText}`;
							} else {
								finalText = addonText;
							}
						}

						finalText = `${finalText} ${restOfTheText}`;

						layer.ok(finalText);

						if (selectedService) {
							this.loggerService.logError(
								'Service purchase success',
								{ productId: selectedService.id },
								Severity.INFO,
							);
						}

						this.trackCurrentStepEvent();

						this.close();
					},
					(errorData: RequestError) => {
						if (errorData.isBlockedOnDemo) {
							layer.close();
							this.accountService.handleDemo();
							return;
						}

						const errorMsg = errorData.errors?.[0]?.message || 'Error when ordering.';

						layer.error(errorMsg);

						if (selectedService) {
							this.loggerService.logError(
								'Service purchase error',
								{ productId: selectedService.id },
								Severity.CRITICAL,
							);
						}
					},
				)
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		}
	}

	@action
	chooseAddon(currentAddon: Billing.Addon) {
		const selectedAddons = this.selectedAddons;

		if (selectedAddons.includes(currentAddon)) {
			this.selectedAddons = selectedAddons.filter((addon) => addon.id !== currentAddon.id);
		} else {
			this.selectedAddons = [...selectedAddons, currentAddon];
		}
	}

	@action
	nextStep() {
		this.trackCurrentStepEvent();

		const steps = this.steps,
			currentStep = this.step;

		let nextStepIndex = steps.findIndex((step) => step === currentStep) + 1;
		if (nextStepIndex === steps.length) {
			nextStepIndex = steps.length - 1;
		}
		this.step = steps[nextStepIndex]!;
		this.scheduleReposition();
	}

	@action
	goBack() {
		const steps = this.steps;

		let prevStepIndex = steps.findIndex((step) => step === this.step) - 1;
		if (prevStepIndex === -1) {
			prevStepIndex = 0;
		}
		this.step = steps[prevStepIndex]!;
		this.scheduleReposition();
	}
	<template>
		<div class="title">{{this.title}}</div>

		<div class="content">

			{{#if this.showText}}
				<p class="p-top">{{this.text}}</p>
			{{/if}}

			{{#if this.isServiceListShown}}
				<div class="account-billing-services">
					{{#each this.serviceOptions as |item|}}
						<AccountBillingServicesOptionComponent
							@model={{item}}
							@selected={{this.selectedService}}
							@onChange={{this.chooseService}}
						/>
					{{/each}}
					<AccountBillingServicesOptionComponent
						@model={{null}}
						@selected={{this.selectedService}}
						@onChange={{this.chooseService}}
					/>
				</div>
			{{else if this.isAddonListShown}}
				<div class="account-billing-addons">
					{{#each this.addonOptions as |item|}}
						<AccountBillingAddonsOptionComponent
							@model={{item}}
							@selectedAddons={{this.selectedAddons}}
							@onChange={{this.chooseAddon}}
						/>
					{{/each}}
				</div>
			{{else}}
				{{#if this.isSummaryEmpty}}

					<div class="summary__empty">
						<CommonUiIconComponent @name="Grid" @size="xl" />
						<div>
							You didn’t select any service or add-ons.<br />
							Don’t worry, you can always select them on your billing page.
						</div>

					</div>

				{{else}}
					<div class="account-billing-summary">
						{{#if this.selectedService}}
							<AccountBillingSummaryRowComponent @model={{this.selectedService}} />
						{{/if}}
						{{#each this.selectedAddons as |item|}}
							<AccountBillingSummaryRowComponent @model={{item}} />
						{{/each}}
					</div>
				{{/if}}

			{{/if}}

			<div class="controls2">
				{{#if this.showBackButton}}
					<div class="controls2__left">
						<SubmitButtonComponent
							@text="Back"
							@theme="grey-empty"
							@noDelay={{true}}
							@icon="ArrowLeft"
							@onTrigger={{this.goBack}}
						/>
					</div>
				{{/if}}

				<div class="controls2__right">
					{{#if this.isSummaryShown}}
						{{#if this.isSummaryEmpty}}
							<SubmitButtonComponent
								@text="Close"
								@size="big"
								@onTrigger={{this.close}}
							/>
						{{else}}
							<SubmitButtonComponent
								@text={{this.finishButtonText}}
								@size="big"
								@onTrigger={{this.finishSummary}}
								@submitInProgress={{this.submitInProgress}}
							/>
						{{/if}}
					{{else}}
						<SubmitButtonComponent
							@text="Continue"
							@onTrigger={{this.nextStep}}
							@size="big"
							@icon="ArrowRight"
						/>
					{{/if}}
				</div>
			</div>

		</div>
	</template>
}
